import { isFulfilled } from "@reduxjs/toolkit";
import React, {
  Dispatch,
  FC,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Cropper from "react-easy-crop";
import { useLocation } from "react-router";
import DefaultImg from "../../../../assets/img/default-image.svg";
import { useImpersonation } from "../../../../contexts/ImpersonationContext";
import Roles from "../../../../models/roles";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import { uploadsProfilePictureThunk } from "../../../../store/auth/thunks";
import { uploadsCentrePictureThunk as clientUploadsCentrePictureThunk } from "../../../../store/centers/thunks";
import { uploadsCentrePictureThunk as adminUploadsCentrePictureThunk, getCandidateData } from "../../../../store/superadmin/thunks";
import CustomButton from "../../Buttons/CustomButton";
import Pencil from "../../Icons/Pencil";
import PopupModal from "../../Popup";
import ErrorMessage from "../../Tooltips/ErrorMessage";
import getCroppedImg from "./getCropImage";

/**
 * Accepted file types for document upload
 */
const ACCEPTED_FILE_TYPES = {
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  png: "image/png",
} as const;

/**
 * Maximum file size in bytes (1MB)
 */
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * ImageUpload Props Interface
 * @interface ImageUploadProps
 */

interface ImageUploadProps {
  /** Error message from API */
  Apierror: string;
  /** set the error meesage we got while uploading */
  setUploadValidationError: Dispatch<SetStateAction<string | null>>;
  /** Custom max file size in bytes */
  maxFileSize?: number;
  /** Optional type of the file */
  type?: string;
  /** Optional function to handle image upload */
  centreId?: number | string | null | undefined;
  previewImage?: string | null;
  candidate_uuid?: string | null;
}

const ImageUpload: FC<ImageUploadProps> = ({
  Apierror,
  setUploadValidationError,
  maxFileSize = MAX_FILE_SIZE,
  type = "",
  centreId = "",
  previewImage = null,
  candidate_uuid = null,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const user = useSelector((state) => state.auth.user);
  const inProgress = useSelector((state) => state.auth.inProgress);
  const inProgressSuperAdmin = useSelector(
    (state) => state.superadmin.inProgress
  );
  const profileData = useSelector((state) => state.onboarding.personaldetails);
  const {isImpersonating} = useImpersonation();
  const reviewCandidateData = useSelector((state) => state.superadmin.reviewCandidateData)

  // Check if the logged-in user is a Super Admin
  const isSuperAdmin = (user?.role?.name as Roles) === Roles.SUPER_ADMIN ||
      (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;

  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [rotation, setRotation] = useState<number>(0);
  const [zoom, setZoom] = useState<number>(1);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCroppier, setShowCroppier] = useState<boolean>(false);
  const [preview, setPreview] = useState<string | undefined | null>(null);
  const [imageName, setImageName] = useState<string>("");

  const isCentreUser = useMemo(
    () => user?.role.name === Roles.CENTER_USER,
    [user]
  );

  const validateFile = useCallback(
    (uploadedFile: any): string | null => {
      if (
        ![...Object.values(ACCEPTED_FILE_TYPES)].includes(uploadedFile.type)
      ) {
        return `Invalid file type. Please upload ${Object.keys(
          ACCEPTED_FILE_TYPES
        )
          .join(", ")
          .toUpperCase()}.`;
      }

      if (uploadedFile.size > maxFileSize) {
        return `File size exceeds the ${maxFileSize / (1024 * 1024)}MB limit.`;
      }

      return null;
    },
    [maxFileSize]
  );

  useEffect(() => {
    setUploadValidationError(error);
  }, [setUploadValidationError, error]);

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file: File | undefined = event.target.files?.[0]; // Use optional chaining to avoid errors
    setError(null);

    if (!file) {
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the file input
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setPreview(null);
      event.target.value = ""; // Reset input
      return;
    }

    if (file) {
      setShowCroppier(!false);
      setImageName(file.name);
      setSelectedFile(URL.createObjectURL(file));
    }
  };

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  useEffect(() => {
    setPreview(previewImage);
  }, [previewImage]);

  const showCroppedImage = useCallback(async () => {
    try {
      const croppedImage: any = await getCroppedImg(
        selectedFile,
        croppedAreaPixels,
        rotation
      );
      const myFile = new File(
        [croppedImage],
        imageName !== "" ? imageName : "image.jpeg",
        {
          type: "image/jpeg",
        }
      );
      const data = new FormData();
      data.append("image", myFile);

      let result = null;
      if (
        type === "centre-image-client" &&
        centreId !== null &&
        centreId !== undefined &&
        centreId !== ""
      ) {
        data.append("centre_id", String(centreId));
        result = await dispatch(clientUploadsCentrePictureThunk(data));
      } else if (
        type === "centre-image-admin" &&
        location.pathname.includes("edit") &&
        centreId !== null &&
        centreId !== undefined &&
        centreId !== ""
      ) {
        data.append("centre_id", String(centreId)); // always send centre id for admin or client
        result = await dispatch(adminUploadsCentrePictureThunk(data));
      } else if (
        type === "centre-image-admin" &&
        (location.pathname.includes("create") ||
          location.pathname.includes("setup"))
      ) {
        result = await dispatch(adminUploadsCentrePictureThunk(data));
      } else if(type === "isSuperAdminCandidateEdit") {
        candidate_uuid &&
          data.append(
            "candidate_uuid",
            candidate_uuid !== null ? String(candidate_uuid) : ""
          );
        result = await dispatch(uploadsProfilePictureThunk(data)); 
        await dispatch(getCandidateData({ candidate_id: reviewCandidateData?.uuid || "" }));
      } else {
        candidate_uuid &&
          data.append(
            "candidate_uuid",
            candidate_uuid !== null ? String(candidate_uuid) : ""
          );
        result = await dispatch(uploadsProfilePictureThunk(data));
      }
      if (isFulfilled(result)) {
        const previewProfilePic = result.payload.data.profile_picture;
        setPreview(previewProfilePic);
        setShowCroppier(!true);
      }
    } catch (e) {
      console.error(e);
    }
  }, [
    dispatch,
    croppedAreaPixels,
    rotation,
    selectedFile,
    setShowCroppier,
    centreId,
    type,
    imageName,
    location,
    candidate_uuid
  ]);

  const onClickClose = () => {
    setShowCroppier(false);
  };

  // Get the review status for the current profile
  const reviewStatus = profileData?.review_status;

  /**
   * Determines if the edit button should be hidden based on status and user role
   * - Super Admin during impersonation: Can ALWAYS edit (show edit button)
   * - Super Admin not impersonating: Can ALWAYS edit (show edit button)
   * - Candidate with "approved" status: CANNOT edit (hide edit button)
   * - Candidate with "waiting" or null status: CAN edit (show edit button)
   */
  const shouldHideEditButton = () => {
    // Super Admin during impersonation can always edit
    if (isImpersonating) {
      return false;
    }
    
    // Super Admin not impersonating (direct edit)
    if (isSuperAdmin) {
      return false;
    }
    
    // For candidates: only hide if status is "approved" (completed and approved)
    // return reviewStatus === 'approved';
  }

  return (
    <>
      {selectedFile && (
        <>
          <PopupModal
            popUpClass={"profilepic-crop"}
            visible={showCroppier}
            showCloseBtn={true}
            onClickClose={onClickClose}
          >
            <div className="d-flex flex-column gap-15">
              <div className="fs-22 fw-400 text-black">Crop Image</div>
              <div className="bg-off-white h-1px w-100"></div>
              <div className="d-flex flex-column gap-15 align-items-center justify-content-center">
                <div
                  className=""
                  style={{
                    position: "relative",
                    width: "192px",
                    height: "192px",
                  }}
                >
                  <Cropper
                    image={selectedFile}
                    crop={crop}
                    rotation={rotation}
                    zoom={zoom}
                    cropShape={"round"}
                    aspect={1}
                    onCropChange={setCrop}
                    onRotationChange={setRotation}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    objectFit="horizontal-cover"
                  />
                </div>
                <CustomButton
                  isLoading={inProgress || inProgressSuperAdmin}
                  contentClass={"d-flex"}
                  onClick={showCroppedImage}
                  variant="primary"
                  className="btn-theme bg-teal text-black fw-600"
                >
                  {" "}
                  Confirm{" "}
                </CustomButton>
              </div>
            </div>
            {Apierror && <ErrorMessage message={Apierror} />}
          </PopupModal>
        </>
      )}

      <div className="position-relative border border-grey-light rounded-circle p-7px">
        {preview ? (
          <img
            src={preview}
            alt="Preview"
            className="w-144px h-144px object-fit-cover rounded-circle d-flex"
          />
        ) : (
          <img
            src={DefaultImg}
            alt="default"
            className="w-144px h-144px object-fit-cover rounded-circle d-flex"
          />
        )}

        {/* File input (hidden) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="visually-hidden"
          id="imageUpload"
        />

        <label
          htmlFor="imageUpload"
          className="cursor-pointer position-absolute position-bottom-center"
        >
          {shouldHideEditButton() ? "" : !isCentreUser ? <Pencil /> : ""}
        </label>
      </div>
    </>
  );
};

export default memo(ImageUpload);
